import {batch} from 'react-redux';
import _, {compact, isArray, orderBy} from 'lodash';
import {getWordBreakClassName, parseMessage} from 'lib/stringHelper';

import {
  isStepVolumeValueUniform,
  processStepVolumeChange,
} from 'contents/ProtocolEditor/SerialDilution/ArrowVolumeBox/SerialDilutionVolumeHelper';
import {setDefaultTipTouchOffDirectionTasks} from 'lib/PipettingProfileItemHelper';
import WellItem from 'models/wellItem';
import {getSerialDilutionStep} from 'stores/selectors/protocolEditorSelectors';
import {getFirstSelectedCardSelector} from 'stores/selectors/cardStateSelector';
import {showSnackbar} from 'stores/actions/appStateAction';
import {getFirstSelectedCard} from 'lib/cardAndTaskHelper';
import {getLabwareDataById} from 'stores/selectors/labwareStateSelectors';
import {
  TaskType,
  TipChannelType,
  CardType,
  WellCount,
  SerialDilutionStep,
  TrayType,
  CardInvalidType,
  ProtocolEditorViewMode,
  ExecutionStatus,
  RuntimeMode,
  SDVolumePropertyKeyEnum,
  Feedback,
  SwipeSelectionDirection,
} from '../../constants/dataEnum';
import * as TemporaryStateType from '../../constants/temporaryStateType';

import * as cardStateAction from '../actions/cardStateAction';
import * as cardSerialDilutionStateAction from '../actions/cardSerialDilutionStateAction';
import * as temporaryStateAction from '../actions/temporaryStateAction';
import * as taskListBusinessAction from './taskListBusinessAction';
import {convertRowToAlphabet, generateCardIDAndTaskIDAndNumber, getNewCardName} from './commonTaskListBusiness';
import * as protocolManagementBusiness from './protocolManagementBusiness';
import {saveState} from './stateBusiness';

import {find, filterToArray, cloneAndMapObject} from '../../lib/objectHelper';
import jsonCopy from '../../lib/jsonCopy';
import {setSelectedMenu, setSerialDilutionStep} from '../actions/protocolEditorAction';
import {
  generateSdRawTasksAndSummary,
  getAdjustedSwipeDirectionByTipCount,
  getSDRawTasksSummaryKeyFromStep,
  getSDAllowedSwipeDirectionOnTrough,
  isChangingSDWellCountAllowed,
  mapToSerialDilutionStep,
  SerialDilutionLabwareValidator,
  isSDPatternAllowFreeSelection,
} from '../../lib/serialDilutionHelper';
import {
  getMinMaxPositionFromTaskList,
  getQuadrant,
  getSkipWellMode,
  isLabware384,
  isLabwareColumnBased,
  isColumnBasedSwipe,
  normalizeHighlightedWell,
  moveRowPosition,
  identifySwipeDirection,
  moveColumnPosition,
  doesLabwareHaveSameDirectionBase,
  SKIP_WELL_MODE,
} from '../../contents/ProtocolEditor/VirtualTray/labwareHelper';
import {
  commonCardStatePropertiesEnum,
  PipettingParameterKeysByTaskType,
  SERIAL_DILUTION_RAW_TASK_PROPERTIES,
} from '../../constants/cardStateEnum';
import {getCardExecutionStatus} from './runtimeBusiness';
import {globalAddUndoItem} from './undoRedoBusinessAction';

export const checkInvalidSdCard = (cardId) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const card = cards[cardId];

  if (!card) return;

  const {tasks: sdTasks} = card;

  if (sdTasks.length) {
    dispatch(cardStateAction.removeCardInvalidType(cardId));
  } else {
    dispatch(cardStateAction.addCardInvalidType(cardId, [CardInvalidType.UNFINISHED]));
  }
};

export const restoreSdWizard = () => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const unfinishedSdCard = Object.values(cards).find(
    (e) => e.type === CardType.SERIAL_DILUTION && e.invalidTypes?.includes(CardInvalidType.UNFINISHED),
  );

  if (unfinishedSdCard) {
    dispatch(setSelectedMenu(ProtocolEditorViewMode.SERIAL_DILUTION));
    dispatch(setSerialDilutionStep(SerialDilutionStep.SERIAL_DILUTION_SETTINGS));
  }
};

export const clearSerialDilutionTemporaryState =
  (avoidSDCardAndTasks = false) =>
  (dispatch) => {
    dispatch(temporaryStateAction.removeTemporaryState(TemporaryStateType.EDIT_FROM_SD_SETTINGS));
    if (avoidSDCardAndTasks) return;
    dispatch(temporaryStateAction.removeTemporaryState(TemporaryStateType.CARDS));
    dispatch(temporaryStateAction.removeTemporaryState(TemporaryStateType.TASKS));
  };

export const removeProcessedTaskIds = (rawTasks = []) => {
  const [rawDiluentSource, rawSampleSource, rawSampleTarget, rawDiluentTarget] = rawTasks;

  const removedProcessedTaskIdTemplate = SERIAL_DILUTION_RAW_TASK_PROPERTIES.filter(
    (prop) => prop !== commonCardStatePropertiesEnum.PROCESSED_TASK_ID,
  );

  const aspirateTaskFieldKey = [
    ...removedProcessedTaskIdTemplate,
    ...PipettingParameterKeysByTaskType[TaskType.ASPIRATE],
  ];

  const dispenseTaskFieldKey = [
    ...removedProcessedTaskIdTemplate,
    ...PipettingParameterKeysByTaskType[TaskType.DISPENSE],
  ];

  const taskDiluentSource = cloneAndMapObject(rawDiluentSource, aspirateTaskFieldKey);
  const taskSampleSource = cloneAndMapObject(rawSampleSource, aspirateTaskFieldKey);
  const taskSampleTarget = rawSampleTarget.map((task) => cloneAndMapObject(task, aspirateTaskFieldKey));
  const taskDiluentTarget = rawDiluentTarget.map((task) => cloneAndMapObject(task, dispenseTaskFieldKey));

  return [
    rawDiluentSource && taskDiluentSource,
    rawSampleSource && taskSampleSource,
    taskSampleTarget,
    taskDiluentTarget,
  ];
};

export function getSdRawTaskIds(cardId) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const card = cards[cardId];

    const taskDiluentSourceId = card?.sdRawTasksSummary?.taskDiluentSource;
    const taskSampleSourceId = card?.sdRawTasksSummary?.taskSampleSource;
    // eslint-disable-next-line no-nested-ternary
    const taskSampleTargetIds = card?.sdRawTasksSummary?.taskSampleTarget
      ? Array.isArray(card?.sdRawTasksSummary?.taskSampleTarget)
        ? card?.sdRawTasksSummary?.taskSampleTarget
        : [card?.sdRawTasksSummary?.taskSampleTarget]
      : [];
    const taskDiluentTargetIds = card?.sdRawTasksSummary?.taskDiluentTargets;

    return [taskDiluentSourceId, taskSampleSourceId, taskSampleTargetIds, taskDiluentTargetIds];
  };
}

function getSdRawTasks(cardId, isRemoveProcessedTaskId) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const selectedCard = find(cards, {ID: cardId});

    const tasks = selectedCard ? {...selectedCard.sdRawTasks} : {};

    const [taskDiluentSourceId, taskSampleSourceId, taskSampleTargetIds, taskDiluentTargetIds] = dispatch(
      getSdRawTaskIds(cardId),
    );

    const taskDiluentSource = _.isNumber(taskDiluentSourceId) && tasks[taskDiluentSourceId];
    const taskSampleSource = _.isNumber(taskSampleSourceId) && tasks[taskSampleSourceId];
    const taskSampleTargets = !!taskSampleTargetIds?.length && filterToArray(tasks, {ID: taskSampleTargetIds});
    const taskDiluentTargets = !!taskDiluentTargetIds?.length && filterToArray(tasks, {ID: taskDiluentTargetIds});

    const sdRawTasks = [taskDiluentSource, taskSampleSource, taskSampleTargets, taskDiluentTargets];

    if (isRemoveProcessedTaskId) return removeProcessedTaskIds(sdRawTasks);

    return sdRawTasks;
  };
}

function generateQuadrantReplicateTasks({
  previousTask,
  currentTask,
  isVerticalDir,
  taskStartingId,
  taskParams,
  reuseTips,
}) {
  const tasks = [];
  let newTaskId = taskStartingId;
  const replicateWell = isVerticalDir ? previousTask.columnTipCount : previousTask.rowTipCount;
  const columnTipCount = isVerticalDir ? previousTask.columnTipCount / replicateWell : previousTask.columnTipCount;
  const rowTipCount = !isVerticalDir ? previousTask.rowTipCount / replicateWell : previousTask.rowTipCount;
  for (let i = 0; i < replicateWell; i++) {
    if (!reuseTips) {
      tasks.push({
        ID: newTaskId++,
        number: SerialDilutionStep.SET_DILUENT_TARGET,
        type: TaskType.EJECT_TIP,
        labwareID: 0,
        columnTipCount,
        rowTipCount,
        isIgnored: currentTask.isIgnored,
      });
      tasks.push({
        ID: newTaskId++,
        number: SerialDilutionStep.SET_DILUENT_TARGET,
        type: TaskType.PICK_TIP,
        columnTipCount,
        rowTipCount,
        labwareID: 2,
        isIgnored: currentTask.isIgnored,
      });
    }
    tasks.push({
      ...previousTask,
      column: isVerticalDir ? previousTask.column + i : previousTask.column,
      row: !isVerticalDir ? convertRowToAlphabet(previousTask.row.charCodeAt(0) - 64 + i) : previousTask.row,
      ID: newTaskId++,
      columnTipCount,
      rowTipCount,
      ...taskParams[TaskType.ASPIRATE],
    });
    tasks.push({
      ...currentTask,
      column: isVerticalDir ? currentTask.column + i : currentTask.column,
      row: !isVerticalDir ? convertRowToAlphabet(currentTask.row.charCodeAt(0) - 64 + i) : currentTask.row,
      ID: newTaskId++,
      columnTipCount,
      rowTipCount,
      ...taskParams[TaskType.DISPENSE],
    });
  }
  return {
    tasks,
    lastTaskID: newTaskId,
  };
}

function shouldEjectOrPickTip({taskDiluentTargetPrev, taskDiluentTarget, lastTask}) {
  let shouldEjectTip = false;
  let shouldPickTip = false;

  if (!taskDiluentTargetPrev) {
    shouldEjectTip = false;
    shouldPickTip = false;
  } else if (lastTask?.type === TaskType.EJECT_TIP) {
    shouldPickTip = true;
  } else if (
    lastTask?.rowTipCount !== taskDiluentTarget?.rowTipCount ||
    lastTask?.columnTipCount !== taskDiluentTarget?.columnTipCount
  ) {
    shouldEjectTip = true;
    shouldPickTip = true;
  }
  return {shouldEjectTip, shouldPickTip};
}

export function generateSdCompleteTasks(cardId) {
  const {getMinTipPositionByLabwareId} = protocolManagementBusiness;
  const getPipettingParameter = (tasks) => ({
    enableMixing: tasks?.enableMixing,
    mixingCycle: tasks?.mixingCycle,
    mixingPositionAspirate: tasks?.mixingPositionAspirate,
    mixingPositionDispense: tasks?.mixingPositionDispense,
    mixingSpeed: tasks?.mixingSpeed,
    mixingVolume: tasks?.mixingVolume,
    retractSpeed: tasks?.retractSpeed,
    speed: tasks?.speed,
    tipPosition: tasks?.tipPosition,
    tipXPositionOffset: tasks?.tipXPositionOffset || 0,
    tipYPositionOffset: tasks?.tipYPositionOffset || 0,
    precisionDispense: tasks?.precisionDispense || false,
    tipTouchOff: tasks?.tipTouchOff || false,
    tipTouchOffDelay: tasks?.tipTouchOffDelay || 0,
    tipTouchOffHeight: tasks?.tipTouchOffHeight || 0,
    tipTouchOffDirectionX: tasks?.tipTouchOffDirectionX || 0,
    tipTouchOffDirectionY: tasks?.tipTouchOffDirectionY || 0,
    dispenseAtWellSide: tasks?.dispenseAtWellSide || false,
  });

  return async (dispatch, getState) => {
    const {protocolState, labwareState} = getState();
    let {cardState: oldCardState} = getState();
    oldCardState = jsonCopy(oldCardState);

    const {cardState, configState, temporaryState} = getState();
    const {cards, tasks} = cardState;

    const tempCards = temporaryState?.editFromSdSettings?.cards || temporaryState.CARDS || null;
    const tempTasks = temporaryState?.editFromSdSettings?.tasks || temporaryState.TASKS || null;

    const card = cards[cardId];
    const filteredTasks = Object.values(tasks).filter((task) => !card.tasks.includes(task.ID));

    if (!card?.sdProperty) {
      return;
    }

    const {replicateWell} = card.sdProperty;

    const [taskDiluentSource, taskSampleSource, taskSampleTargets, taskDiluentTargets] = dispatch(
      getSdRawTasks(cardId, true),
    );
    const taskSampleTargetLabwareIds = _.uniq(taskSampleTargets.map((task) => task.labwareID));
    const diluentTargetLabwareIds = _.uniq(taskDiluentTargets.map((task) => task.labwareID));

    const aspirateDiluentSourceMinTipPosition = dispatch(getMinTipPositionByLabwareId(taskDiluentSource.labwareID));
    const aspirateSampleSourceMinTipPosition = dispatch(getMinTipPositionByLabwareId(taskSampleSource.labwareID));
    const dispenseSampleSourceMinTipPosition = dispatch(getMinTipPositionByLabwareId(taskSampleTargets[0].labwareID));

    let aspirateDiluentSourcePipettingParameters = {
      tipPosition: aspirateDiluentSourceMinTipPosition,
      mixingPosition: aspirateDiluentSourceMinTipPosition,
    };
    let aspirateSampleSourcePipettingParameters = {
      tipPosition: aspirateSampleSourceMinTipPosition,
      mixingPosition: aspirateSampleSourceMinTipPosition,
    };
    let dispenseSampleSourcePipettingParameters = {
      tipPosition: dispenseSampleSourceMinTipPosition,
      mixingPosition: dispenseSampleSourceMinTipPosition,
    };
    const aspirateDiluentTargetPipettingParameters = {};
    const dispenseDiluentSourcePipettingParameters = {};
    const dispenseDiluentTargetPipettingParameters = {};

    diluentTargetLabwareIds.forEach((labwareID) => {
      const diluentTargeteMinTipPosition = dispatch(getMinTipPositionByLabwareId(labwareID));
      aspirateDiluentTargetPipettingParameters[labwareID] = {
        tipPosition: diluentTargeteMinTipPosition,
        mixingPosition: diluentTargeteMinTipPosition,
      };
      dispenseDiluentSourcePipettingParameters[labwareID] = {
        tipPosition: diluentTargeteMinTipPosition,
        mixingPosition: diluentTargeteMinTipPosition,
      };
      dispenseDiluentTargetPipettingParameters[labwareID] = {};
    });

    let cardTaskIds = [];
    let cardTasks = {};

    // pipetting parameters from temporaray state
    if (tempCards && tempTasks) {
      cardTasks = _.pick(tempTasks, tempCards.tasks);
      cardTaskIds = tempCards.tasks;
    } else {
      cardTasks = oldCardState.tasks;
      const oldCards = oldCardState.cards;
      cardTaskIds = oldCards[cardId]?.tasks;
    }

    if (cardTaskIds.length && cardTasks) {
      const aspirateDiluentSource = find(cardTasks, {
        ID: cardTaskIds,
        number: SerialDilutionStep.SET_DILUENT_SOURCE,
        type: TaskType.ASPIRATE,
      });
      if (aspirateDiluentSource) {
        aspirateDiluentSourcePipettingParameters = getPipettingParameter(aspirateDiluentSource);
      }

      const aspirateSampleSource = find(cardTasks, {
        ID: cardTaskIds,
        number: SerialDilutionStep.SET_SAMPLE_SOURCE,
        type: TaskType.ASPIRATE,
      });
      if (aspirateSampleSource) {
        aspirateSampleSourcePipettingParameters = getPipettingParameter(aspirateSampleSource);
      }

      const dispenseSampleSource = find(cardTasks, {
        ID: cardTaskIds,
        number: SerialDilutionStep.SET_SAMPLE_SOURCE,
        type: TaskType.DISPENSE,
      });
      if (dispenseSampleSource) {
        dispenseSampleSourcePipettingParameters = getPipettingParameter(dispenseSampleSource, true);

        const aspirateDiluentTarget = find(cardTasks, {
          ID: cardTaskIds,
          number: SerialDilutionStep.SET_DILUENT_TARGET,
          type: TaskType.ASPIRATE,
          labwareID: dispenseSampleSource.labwareID,
        });
        if (aspirateDiluentTarget) {
          aspirateDiluentTargetPipettingParameters[dispenseSampleSource.labwareID] =
            getPipettingParameter(aspirateDiluentTarget);
        }
      }

      diluentTargetLabwareIds.forEach((labwareID) => {
        const dispenseDiluentSource = find(cardTasks, {
          ID: cardTaskIds,
          number: SerialDilutionStep.SET_DILUENT_SOURCE,
          type: TaskType.DISPENSE,
          labwareID,
        });

        if (dispenseDiluentSource) {
          dispenseDiluentSourcePipettingParameters[labwareID] = getPipettingParameter(dispenseDiluentSource, true);
        }

        const aspirateDiluentTarget = find(cardTasks, {
          ID: cardTaskIds,
          number: SerialDilutionStep.SET_DILUENT_TARGET,
          type: TaskType.ASPIRATE,
          labwareID,
        });
        if (aspirateDiluentTarget) {
          aspirateDiluentTargetPipettingParameters[labwareID] = getPipettingParameter(aspirateDiluentTarget);
        }

        const dispenseDiluentTarget = find(cardTasks, {
          ID: cardTaskIds,
          number: SerialDilutionStep.SET_DILUENT_TARGET,
          type: TaskType.DISPENSE,
          labwareID,
        });
        if (dispenseDiluentTarget) {
          dispenseDiluentTargetPipettingParameters[labwareID] = getPipettingParameter(dispenseDiluentTarget, true);
        }
      });
    }

    if (!taskDiluentTargets && !taskSampleTargets) {
      // must be defined, part of required step
      return;
    }

    const reuseTips = card?.sdProperty?.reuseTips;
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
    const maxTipVolume =
      protocolTrayType === TrayType.HIGH_VOLUME_CADDY
        ? configState.maxAspirateHighVolume.value
        : configState.maxAspirateVolume.value;

    const aliquotVolume = parseFloat(card.sdProperty.aliquotVolume ?? 0);
    const sampleVolume = parseFloat(card.sdProperty.sampleVolume ?? 0);
    const diluentVolume = parseFloat(
      typeof card.sdProperty.diluentVolume === 'number' ? card.sdProperty.diluentVolume : 0,
    );
    const stepProperties = card.sdProperty?.stepProperties ?? [];
    const serialDilutionStep = card.sdProperty?.step ?? 0;

    const mixingVolume = Math.min((aliquotVolume || sampleVolume) + diluentVolume, maxTipVolume);

    const {rowTipCount: rowTipCountTargetDiluent, columnTipCount: columnTipCountTargetDiluent} = taskDiluentTargets[0];

    const {rowTipCount, columnTipCount} = taskSampleTargets[0];

    const {equalDiluentVolume} = card.sdProperty;

    const processedTasks = {
      diluentSource: undefined,
      sampleSource: undefined,
      sampleTarget: [],
      diluentTarget: [],
    };

    const completeTasks = [];

    const isMultiQuadrant = taskSampleTargets.length > 1;

    // eslint-disable-next-line prefer-const
    let [, newTaskId] = generateCardIDAndTaskIDAndNumber(cards, filteredTasks, '', cardId);

    // diluent preparation tasks
    if (taskDiluentSource && !taskDiluentSource.skipped) {
      if (taskDiluentTargets && taskDiluentTargets.length > 0) {
        completeTasks.push({
          ID: newTaskId++,
          number: SerialDilutionStep.SET_DILUENT_SOURCE,
          type: TaskType.PICK_TIP,
          labwareID: 2,
          isIgnored: taskDiluentSource.isIgnored,
          rowTipCount,
          columnTipCount,
        });

        const isPatternColumnBased = isColumnBasedSwipe({row: rowTipCount, column: columnTipCount});

        for (let i = 1; i <= serialDilutionStep; i += 1) {
          const diluentTasks = taskDiluentTargets.filter((task) => task.step === i);

          for (let j = 0; j < diluentTasks.length; j += 1) {
            const task = diluentTasks[j];

            const {columnTipCount: diluentColTipCount, rowTipCount: diluentRowTipCount} = task;

            const replicateTransferColumn = Math.abs(diluentColTipCount - columnTipCount);
            const replicateTransferRow = Math.abs(diluentRowTipCount - rowTipCount);

            const stepProperty = stepProperties.find((prop) => prop.step === task.step);
            const stepDiluentVolume = stepProperty?.stepDiluentVolume;
            const stepSampleVolume = stepProperty?.stepSampleVolume;

            const currentStepBaseDiluentProperty = {
              number: SerialDilutionStep.SET_DILUENT_SOURCE,
              volume: stepDiluentVolume,
              mixingVolume: Math.min(stepSampleVolume + stepDiluentVolume, maxTipVolume),
              isIgnored: taskDiluentSource.isIgnored,
              rowTipCount,
              columnTipCount,
            };

            const currentStepDiluentTransfer = {
              aspirate: {
                ...taskDiluentSource,
                ...currentStepBaseDiluentProperty,
                ...aspirateDiluentSourcePipettingParameters,
              },
              dispense: {
                ...task,
                ...currentStepBaseDiluentProperty,
                ...dispenseDiluentSourcePipettingParameters[task.labwareID],
              },
            };

            const maxReplicateTarget = Math.max(replicateTransferColumn, replicateTransferRow);

            for (let iReplicate = 0; iReplicate <= maxReplicateTarget; iReplicate += 1) {
              const {row: dilutionStepRow, column: dilutionStepColumn, labwareID} = task;
              const labware = getLabwareDataById(labwareID)(getState());

              let dispenseRow = dilutionStepRow;
              let dispenseColumn = dilutionStepColumn;

              const is384 = isLabware384(labware);
              const [skipWellMode] = getSkipWellMode(rowTipCount, columnTipCount, isMultiQuadrant, is384);

              if (isPatternColumnBased) {
                dispenseColumn = moveColumnPosition({
                  labware,
                  initialPosition: dispenseColumn,
                  moveCountNumber: iReplicate,
                  skipWellMode,
                });
              } else {
                dispenseRow = moveRowPosition({
                  labware,
                  initialPosition: dispenseRow,
                  moveCountNumber: iReplicate,
                  skipWellMode,
                });
              }

              const diluentSourceAspirateTask = {...currentStepDiluentTransfer.aspirate, ID: newTaskId++};
              const diluentSourceDispenseTask = {
                ...currentStepDiluentTransfer.dispense,
                ID: newTaskId++,
                row: dispenseRow,
                column: dispenseColumn,
              };

              completeTasks.push(diluentSourceAspirateTask, diluentSourceDispenseTask);

              if (!processedTasks.diluentSource) {
                processedTasks.diluentSource = diluentSourceAspirateTask;
              }
            }
          }
        }

        completeTasks.push({
          ID: newTaskId++,
          number: SerialDilutionStep.SET_DILUENT_SOURCE,
          type: TaskType.EJECT_TIP,
          labwareID: 0,
          isIgnored: taskDiluentSource.isIgnored,
          rowTipCount,
          columnTipCount,
        });
      }
    }

    // NOTE: even though user can only select one sample target, the sample target still needs to be defined as array
    //       to handle full row/column selection on 384 well, which then produce 2 aspirates sample target tasks (2 quadrants).
    for (let j = 0; j < taskSampleTargets.length; j += 1) {
      // sample preparation tasks
      if (reuseTips) {
        completeTasks.push({
          ID: newTaskId++,
          number: SerialDilutionStep.SET_SAMPLE_SOURCE,
          type: TaskType.PICK_TIP,
          columnTipCount,
          rowTipCount,
          labwareID: 2,
          isIgnored: card.isIgnored,
        });
      }

      if (taskSampleSource && !taskSampleSource.skipped) {
        const taskSampleTarget = taskSampleTargets[j];
        if (!reuseTips) {
          completeTasks.push({
            ID: newTaskId++,
            number: SerialDilutionStep.SET_SAMPLE_SOURCE,
            type: TaskType.PICK_TIP,
            columnTipCount,
            rowTipCount,
            labwareID: 2,
            isIgnored: taskSampleSource.isIgnored,
          });
        }

        completeTasks.push({
          ...taskSampleSource,
          ID: newTaskId++,
          number: SerialDilutionStep.SET_SAMPLE_SOURCE,
          volume: sampleVolume,
          mixingVolume,
          columnTipCount,
          rowTipCount,
          isIgnored: taskSampleSource.isIgnored,
          ...aspirateSampleSourcePipettingParameters,
        });

        processedTasks.sampleSource = completeTasks[completeTasks.length - 1];

        completeTasks.push({
          ...taskSampleTarget,
          ID: newTaskId++,
          type: TaskType.DISPENSE,
          number: SerialDilutionStep.SET_SAMPLE_SOURCE,
          volume: sampleVolume,
          mixingVolume,
          isIgnored: taskSampleTarget.isIgnored,
          ...dispenseSampleSourcePipettingParameters,
        });

        if (!reuseTips) {
          completeTasks.push({
            ID: newTaskId++,
            number: SerialDilutionStep.SET_SAMPLE_SOURCE,
            type: TaskType.EJECT_TIP,
            labwareID: 0,
            columnTipCount,
            rowTipCount,
            isIgnored: taskSampleSource.isIgnored,
          });
        }
      }

      // serial dilution tasks
      if (taskDiluentTargets && taskDiluentTargets.length > 0) {
        for (let i = 1; i <= serialDilutionStep; i += 1) {
          const stepTaskDiluentTargetPrevs = taskDiluentTargets.filter((task) => task.step === i - 1);
          const stepTaskDiluentTargets = taskDiluentTargets.filter((task) => task.step === i);

          // j is the quadrant number for 384 well plate
          const taskDiluentTargetPrev = stepTaskDiluentTargetPrevs[j] ?? stepTaskDiluentTargetPrevs[0];
          const taskDiluentTarget = stepTaskDiluentTargets[j];
          const taskSampleTarget = taskSampleTargets[j] ?? taskSampleTargets[0];

          const stepProperty = stepProperties.find((prop) => prop.step === taskDiluentTarget.step);
          const stepDiluentVolume = stepProperty?.stepDiluentVolume ?? diluentVolume;
          const stepSampleVolume = stepProperty?.stepSampleVolume ?? (aliquotVolume || sampleVolume);
          const taskNumber = i === 1 ? SerialDilutionStep.SET_SAMPLE_TARGET : SerialDilutionStep.SET_DILUENT_TARGET;

          const labware = find(labwareState, {LabwareID: taskDiluentTarget.labwareID});
          const is384 = isLabware384(labware);
          const isVerticalTipDirection = columnTipCount > 1 && rowTipCount === 1;
          const isHorizontalTipDirection = rowTipCount > 1 && columnTipCount === 1;
          const isHorizontalQuadrantSelection = is384 && isVerticalTipDirection && replicateWell > labware.rowCount / 2;
          const isVerticalQuadrantSelection = is384 && isHorizontalTipDirection && replicateWell > labware.colCount / 2;

          const useTDTaskTips =
            i !== 1 && replicateWell > 1 && !isHorizontalQuadrantSelection && !isVerticalQuadrantSelection;

          const computedColumnTipCount = useTDTaskTips ? columnTipCountTargetDiluent : columnTipCount;
          const computedRowTipCount = useTDTaskTips ? rowTipCountTargetDiluent : rowTipCount;
          const {shouldEjectTip, shouldPickTip} = shouldEjectOrPickTip({
            taskDiluentTargetPrev,
            taskDiluentTarget,
            lastTask: completeTasks[completeTasks.length - 1],
          });

          if (shouldEjectTip) {
            completeTasks.push({
              ID: newTaskId++,
              number: SerialDilutionStep.SET_DILUENT_TARGET,
              type: TaskType.EJECT_TIP,
              columnTipCount: completeTasks[completeTasks.length - 1].columnTipCount,
              rowTipCount: completeTasks[completeTasks.length - 1].rowTipCount,
              labwareID: 0,
              isIgnored: taskDiluentTargetPrev.isIgnored,
              changeTip: !reuseTips,
            });
          }

          if (!reuseTips || shouldPickTip) {
            completeTasks.push({
              ID: newTaskId++,
              number: SerialDilutionStep.SET_DILUENT_TARGET,
              type: TaskType.PICK_TIP,
              columnTipCount: computedColumnTipCount,
              rowTipCount: computedRowTipCount,
              labwareID: 2,
              isIgnored: taskDiluentTarget.isIgnored,
            });
          }

          if ((!isVerticalQuadrantSelection && !isHorizontalQuadrantSelection) || i === 1) {
            completeTasks.push({
              ...(i === 1 ? taskSampleTarget : taskDiluentTargetPrev),
              ID: newTaskId++,
              number: taskNumber,
              type: TaskType.ASPIRATE,
              volume: stepSampleVolume,
              mixingVolume: Math.min(stepSampleVolume + stepDiluentVolume, maxTipVolume),
              isIgnored: taskDiluentTarget.isIgnored,
              columnTipCount:
                taskNumber === SerialDilutionStep.SET_DILUENT_TARGET ? columnTipCountTargetDiluent : columnTipCount,
              rowTipCount:
                taskNumber === SerialDilutionStep.SET_DILUENT_TARGET ? rowTipCountTargetDiluent : rowTipCount,
              ...(i === 1
                ? aspirateDiluentTargetPipettingParameters[taskSampleTarget.labwareID]
                : aspirateDiluentTargetPipettingParameters[taskDiluentTargetPrev.labwareID]),
            });
          }

          if (taskNumber === SerialDilutionStep.SET_SAMPLE_TARGET) {
            processedTasks.sampleTarget.push(completeTasks[completeTasks.length - 1]);
          }

          let dilutionDispense;

          if (i === 1 && (rowTipCount !== rowTipCountTargetDiluent || columnTipCount !== columnTipCountTargetDiluent)) {
            const unEqualParam =
              rowTipCount !== rowTipCountTargetDiluent ? rowTipCountTargetDiluent : columnTipCountTargetDiluent;

            completeTasks.push({
              ...taskDiluentTarget,
              ID: newTaskId++,
              number: SerialDilutionStep.SET_DILUENT_TARGET,
              type: TaskType.DISPENSE,
              enableMixing: true,
              volume: stepSampleVolume,
              mixingVolume: Math.min(stepSampleVolume + stepDiluentVolume, maxTipVolume),
              isIgnored: taskDiluentTarget.isIgnored,
              columnTipCount,
              rowTipCount,
              ...dispenseDiluentTargetPipettingParameters[taskDiluentTarget.labwareID],
            });
            dilutionDispense = completeTasks[completeTasks.length - 1];

            for (let repSeq = 2; repSeq <= unEqualParam; repSeq++) {
              let dispenseColumn = taskDiluentTarget.column;
              let dispenseRow = taskDiluentTarget.row;

              const [skipWellMode] = getSkipWellMode(rowTipCount, columnTipCount, isMultiQuadrant, is384);

              if (columnTipCount !== columnTipCountTargetDiluent) {
                dispenseColumn = moveColumnPosition({
                  labware,
                  initialPosition: dispenseColumn,
                  moveCountNumber: repSeq - 1,
                  skipWellMode,
                });
              }

              if (rowTipCount !== rowTipCountTargetDiluent) {
                dispenseRow = moveRowPosition({
                  labware,
                  initialPosition: dispenseRow,
                  moveCountNumber: repSeq - 1,
                  skipWellMode,
                });
              }

              completeTasks.push({
                ...taskSampleTarget,
                ID: newTaskId++,
                number: taskNumber,
                type: TaskType.ASPIRATE,
                volume: stepSampleVolume,
                mixingVolume: Math.min(stepSampleVolume + stepDiluentVolume, maxTipVolume),
                isIgnored: taskDiluentTarget.isIgnored,
                columnTipCount,
                rowTipCount,
                ...aspirateDiluentTargetPipettingParameters[taskSampleTarget.labwareID],
              });
              completeTasks.push({
                ...taskDiluentTarget,
                column: dispenseColumn,
                row: dispenseRow,
                ID: newTaskId++,
                number: SerialDilutionStep.SET_DILUENT_TARGET,
                type: TaskType.DISPENSE,
                enableMixing: true,
                volume: stepSampleVolume,
                mixingVolume: Math.min(stepSampleVolume + stepDiluentVolume, maxTipVolume),
                isIgnored: taskDiluentTarget.isIgnored,
                columnTipCount,
                rowTipCount,
                ...dispenseDiluentTargetPipettingParameters[taskDiluentTarget.labwareID],
              });
            }
          } else if (i !== 1 && (isVerticalQuadrantSelection || isHorizontalQuadrantSelection) && replicateWell > 1) {
            const taskParams = {
              [TaskType.ASPIRATE]: {
                number: SerialDilutionStep.SET_DILUENT_TARGET,
                type: TaskType.ASPIRATE,
                volume: stepSampleVolume,
                mixingVolume: Math.min(stepSampleVolume + stepDiluentVolume, maxTipVolume),
                isIgnored: taskDiluentTarget.isIgnored,
                ...aspirateDiluentTargetPipettingParameters[taskDiluentTargetPrev.labwareID],
              },
              [TaskType.DISPENSE]: {
                number: SerialDilutionStep.SET_DILUENT_TARGET,
                type: TaskType.DISPENSE,
                enableMixing: true,
                volume: stepSampleVolume,
                mixingVolume: Math.min(stepSampleVolume + stepDiluentVolume, maxTipVolume),
                isIgnored: taskDiluentTarget.isIgnored,
                ...dispenseDiluentTargetPipettingParameters[taskDiluentTarget.labwareID],
              },
            };
            const taskInformation = generateQuadrantReplicateTasks({
              previousTask: taskDiluentTargetPrev,
              currentTask: taskDiluentTarget,
              isVerticalDir: isVerticalQuadrantSelection,
              taskStartingId: newTaskId,
              taskParams,
              reuseTips,
            });
            completeTasks.push(...taskInformation.tasks);
            newTaskId = taskInformation.lastTaskID;
            dilutionDispense = taskParams[TaskType.DISPENSE];
          } else {
            completeTasks.push({
              ...taskDiluentTarget,
              ID: newTaskId++,
              number: SerialDilutionStep.SET_DILUENT_TARGET,
              type: TaskType.DISPENSE,
              enableMixing: true,
              volume: stepSampleVolume,
              mixingVolume: Math.min(stepSampleVolume + stepDiluentVolume, maxTipVolume),
              isIgnored: taskDiluentTarget.isIgnored,
              columnTipCount: columnTipCountTargetDiluent,
              rowTipCount: rowTipCountTargetDiluent,
              ...dispenseDiluentTargetPipettingParameters[taskDiluentTarget.labwareID],
            });
            dilutionDispense = completeTasks[completeTasks.length - 1];
          }

          processedTasks.diluentTarget.push({
            ...dilutionDispense,
            columnTipCount: columnTipCountTargetDiluent,
            rowTipCount: rowTipCountTargetDiluent,
          });

          if (i === serialDilutionStep && equalDiluentVolume) {
            let remainingVolume = stepSampleVolume;

            for (let index = 0; index < Math.ceil(stepSampleVolume / maxTipVolume); index++) {
              if (index > 0) {
                completeTasks.push({
                  ID: newTaskId++,
                  number: SerialDilutionStep.SET_DILUENT_TARGET,
                  type: TaskType.EJECT_TIP,
                  labwareID: 0,
                  columnTipCount: computedColumnTipCount,
                  rowTipCount: computedRowTipCount,
                  isIgnored: taskDiluentTarget.isIgnored,
                  changeTip: !reuseTips,
                });

                completeTasks.push({
                  ID: newTaskId++,
                  number: SerialDilutionStep.SET_DILUENT_TARGET,
                  type: TaskType.PICK_TIP,
                  columnTipCount: computedColumnTipCount,
                  rowTipCount: computedRowTipCount,
                  labwareID: 2,
                  isIgnored: taskDiluentTarget.isIgnored,
                });
              }

              completeTasks.push({
                ...taskDiluentTarget,
                ID: newTaskId++,
                number: SerialDilutionStep.SET_DILUENT_TARGET,
                type: TaskType.ASPIRATE,
                volume: remainingVolume > maxTipVolume ? maxTipVolume : remainingVolume,
                mixingVolume,
                isIgnored: taskDiluentTarget.isIgnored,
                columnTipCount: computedColumnTipCount,
                rowTipCount: computedRowTipCount,
                ...aspirateDiluentTargetPipettingParameters[taskDiluentTarget.labwareID],
              });

              remainingVolume -= maxTipVolume;
            }
          }

          if (!reuseTips) {
            completeTasks.push({
              ID: newTaskId++,
              number: SerialDilutionStep.SET_DILUENT_TARGET,
              type: TaskType.EJECT_TIP,
              labwareID: 0,
              columnTipCount: computedColumnTipCount,
              rowTipCount: computedRowTipCount,
              isIgnored: taskDiluentTarget.isIgnored,
              changeTip: !reuseTips,
            });
          }
        }
      }

      if (reuseTips) {
        completeTasks.push({
          ID: newTaskId++,
          number: SerialDilutionStep.SET_DILUENT_TARGET,
          type: TaskType.EJECT_TIP,
          labwareID: 0,
          columnTipCount: completeTasks[completeTasks.length - 1].columnTipCount,
          rowTipCount: completeTasks[completeTasks.length - 1].rowTipCount,
          isIgnored: card.isIgnored,
        });
      }
    }

    const mappedProcessedTasks = mapToSerialDilutionStep(
      processedTasks.diluentSource,
      processedTasks.sampleSource,
      processedTasks.sampleTarget,
      processedTasks.diluentTarget,
    );

    const {sdRawTasks: newSdRawTasks, sdRawTasksSummary: newSdRawTasksSummary} = generateSdRawTasksAndSummary(
      mappedProcessedTasks,
      serialDilutionStep,
    );

    batch(() => {
      dispatch(cardStateAction.removeAllCardTask(cardId));
      dispatch(cardSerialDilutionStateAction.restoreCardSdRawTask(cardId, newSdRawTasks));
      dispatch(cardSerialDilutionStateAction.setSdRawTasksSummary(cardId, newSdRawTasksSummary));
      dispatch(cardStateAction.batchAddCardTask(cardId, card.name, CardType.SERIAL_DILUTION, completeTasks));
      dispatch(
        taskListBusinessAction.smarterDefaultDispenseTipPosition(
          _.uniq([...taskSampleTargetLabwareIds, ...diluentTargetLabwareIds]),
        ),
      );

      dispatch(checkInvalidSdCard(cardId));
      dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(cardId));
    });
  };
}

function getTaskTypeBySdStep(sdStep) {
  switch (sdStep) {
    case SerialDilutionStep.SET_SAMPLE_SOURCE:
    case SerialDilutionStep.SET_DILUENT_SOURCE:
    case SerialDilutionStep.SET_SAMPLE_TARGET:
      return TaskType.ASPIRATE;
    default:
      return TaskType.DISPENSE;
  }
}

export function createSdCardIifNotExist(cardId) {
  return async (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const card = cards[cardId];

    if (!card) {
      const cardName = getNewCardName(cards, CardType.SERIAL_DILUTION);
      let newCardId = cardId;

      if (!newCardId) {
        [newCardId] = generateCardIDAndTaskIDAndNumber(cards, tasks, CardType.SERIAL_DILUTION);
      }

      await dispatch(cardSerialDilutionStateAction.addSdCard(newCardId, cardName, CardType.SERIAL_DILUTION));
      dispatch(cardStateAction.setCardIsSelected(newCardId));
      dispatch(
        cardSerialDilutionStateAction.setSdPropertyObject(newCardId, {reuseTips: true, isSetSampleToAllSteps: false}),
      );
      dispatch(checkInvalidSdCard(newCardId));
      await dispatch(saveState());

      return null;
    }

    if (!card.isSelected) {
      dispatch(cardStateAction.setCardIsSelected(cardId));
    }

    return card;
  };
}

export function setSdRawTask(sdStep, rowTipCount, columnTipCount, labwareID, rowColumnList, skipped = false) {
  return (dispatch, getState) => {
    const {cardState, configState, labwareState} = getState();
    const {cards, tasks} = cardState;

    const labware = find(labwareState, {LabwareID: labwareID});
    const [cardID, taskID] = generateCardIDAndTaskIDAndNumber(cards, tasks, CardType.SERIAL_DILUTION);

    let taskToAdd = {};

    if (skipped) {
      taskToAdd = {
        [taskID]: {
          ID: taskID,
          skipped: true,
          labwareID,
        },
      };
    } else {
      rowColumnList.forEach((rowColumn, idx) => {
        const {row, column} = rowColumn;
        const newRowTipCount = rowColumn?.tipCount?.row || rowTipCount;
        const newColumnTipCount = rowColumn?.tipCount?.column || columnTipCount;

        const [actualRow, actualColumn] = dispatch(
          taskListBusinessAction.getWellTarget(labwareID, TipChannelType.TIP_CHANNEL_1, row, column),
        );
        const type = getTaskTypeBySdStep(sdStep);

        let labwareWellCount = WellCount.WELL_384;
        if (labware) {
          const {colCount, rowCount} = labware;
          labwareWellCount = colCount * rowCount;
        }
        const configId = `${labwareWellCount}Well`;
        let labwareConfig = configState;
        if (configId in configState) {
          const key = type.toLowerCase();
          labwareConfig = configState[configId][key];
        }

        const minTipPosition = dispatch(protocolManagementBusiness.getMinTipPositionByLabwareId(labwareID));
        const newTipPosition =
          type === TaskType.DISPENSE &&
          sdStep === SerialDilutionStep.SET_DILUENT_TARGET &&
          labwareConfig.tipPosition >= minTipPosition
            ? labwareConfig.tipPosition
            : minTipPosition;
        const newMixingPosition =
          labwareConfig.mixingPosition >= minTipPosition ? labwareConfig.mixingPosition : minTipPosition;

        let task = {
          ID: taskID + idx,
          number: sdStep,
          type,
          labwareID,
          row: actualRow,
          column: actualColumn,
          rowTipCount: newRowTipCount,
          columnTipCount: newColumnTipCount,
          enableMixing: false,
          mixingCycle: 3,
          mixingVolume: labwareConfig.mixingVolume || 13,
          tipPosition: newTipPosition,
          mixingPositionAspirate: newMixingPosition,
          mixingPositionDispense: newMixingPosition,
          speed: labwareConfig.mixingSpeed || 3,
          retractSpeed: configState.rangeRetractAfterAspirateSpeed.default || 20,
          mixingSpeed: labwareConfig.mixingSpeed || 3,
          tipTouchOff: false,
          tipTouchOffDelay: 0,
          tipTouchOffHeight: labware?.wellDepth,
        };
        task = setDefaultTipTouchOffDirectionTasks(labware, task);

        taskToAdd = {
          ...taskToAdd,
          [task.ID]: task,
        };
      });
    }

    batch(async () => {
      await dispatch(createSdCardIifNotExist(cardID));

      switch (sdStep) {
        case SerialDilutionStep.SET_SAMPLE_SOURCE:
          dispatch(cardSerialDilutionStateAction.addSdRawTask(cardID, taskToAdd));
          dispatch(cardSerialDilutionStateAction.setSdRawTasksSummarySampleSource(cardID, taskID));
          break;
        case SerialDilutionStep.SET_SAMPLE_TARGET: {
          const taskIds = Object.keys(taskToAdd).map((id) => Number(id));
          dispatch(cardSerialDilutionStateAction.addSdRawTask(cardID, taskToAdd));
          dispatch(cardSerialDilutionStateAction.setSdRawTasksSummarySampleTarget(cardID, taskIds));
          break;
        }
        case SerialDilutionStep.SET_DILUENT_SOURCE:
          dispatch(cardSerialDilutionStateAction.addSdRawTask(cardID, taskToAdd));
          dispatch(cardSerialDilutionStateAction.setSdRawTasksSummaryDiluentSource(cardID, taskID));
          break;
        case SerialDilutionStep.SET_DILUENT_TARGET:
          {
            let taskDiluentTargetIds = cards[cardID]?.sdRawTasksSummary?.taskDiluentTargets || [];
            taskDiluentTargetIds = [...taskDiluentTargetIds, taskID];

            dispatch(cardSerialDilutionStateAction.addSdRawTask(cardID, taskToAdd));
            dispatch(cardSerialDilutionStateAction.setSdRawTasksSummaryDiluentTargets(cardID, taskDiluentTargetIds));

            const {cardState: cardState2} = getState();
            const {sdProperty, sdRawTasksSummary} = find(cardState2.cards, {ID: cardID});

            if (sdProperty) {
              dispatch(
                cardSerialDilutionStateAction.setCardSdProperty(cardID, {
                  ...sdProperty,
                  step: sdRawTasksSummary.taskDiluentTargets.length,
                }),
              );
            }
          }
          break;
        default:
          break;
      }

      dispatch(saveState());
    });
  };
}

// rowColumnList = [{ row, column }, { row, column }, ...]
export function setSdRawDiluentTargetTasks(rowTipCount, columnTipCount, labwareID, rowColumnList, isMultiQuadrant) {
  return (dispatch, getState) => {
    const {cardState, configState, labwareState} = getState();
    const {cards, tasks} = cardState;

    const [cardID, taskID] = generateCardIDAndTaskIDAndNumber(cards, tasks, CardType.SERIAL_DILUTION);

    const sdStep = SerialDilutionStep.SET_DILUENT_TARGET;
    const type = getTaskTypeBySdStep(sdStep);
    const labware = find(labwareState, {LabwareID: labwareID});
    const is384 = isLabware384(labware);

    let labwareWellCount = WellCount.WELL_384;
    if (labware) {
      const {colCount, rowCount} = labware;
      labwareWellCount = colCount * rowCount;
    }

    const configId = `${labwareWellCount}Well`;
    let labwareConfig = configState;
    if (configId in configState) {
      const key = type.toLowerCase();
      labwareConfig = configState[configId][key];
    }

    const minTipPosition = dispatch(protocolManagementBusiness.getMinTipPositionByLabwareId(labwareID));
    const newTipPosition = labwareConfig.tipPosition >= minTipPosition ? labwareConfig.tipPosition : minTipPosition;
    const newMixingPosition =
      labwareConfig.mixingPosition >= minTipPosition ? labwareConfig.mixingPosition : minTipPosition;

    let task = {
      ID: taskID,
      number: sdStep,
      type,
      labwareID,
      rowTipCount,
      columnTipCount,
      enableMixing: false,
      mixingCycle: 3,
      mixingVolume: 0.1,
      tipPosition: newTipPosition,
      mixingPositionAspirate: newMixingPosition,
      mixingPositionDispense: newMixingPosition,
      speed: labwareConfig.mixingSpeed || 3,
      retractSpeed: configState.rangeRetractAfterAspirateSpeed.default || 20,
      mixingSpeed: labwareConfig.mixingSpeed || 3,
      tipTouchOff: false,
      tipTouchOffDelay: 0,
      tipTouchOffHeight: labware?.wellDepth,
    };

    task = setDefaultTipTouchOffDirectionTasks(labware, task);

    const {sdProperty} = find(cardState.cards, {ID: cardID});
    let step = sdProperty?.step ?? 0;

    const sdStepTasks = {};
    const registeredAxisBasedSelectedWell = {};

    // to identify swipe direction we need to have 2 reference tasks to compare
    const firstReferenceTask = rowColumnList[0];
    let secondReferenceTask = rowColumnList[1];

    // second reference task should depend on whether the selection is multi-quadrant or not,
    // as to not wrongly get the multi-quadrant direction instead of the real swipe direction
    if (isMultiQuadrant) {
      const unreplicatedTipCount = dispatch(unReplicateTipCount({rowTipCount, columnTipCount}));
      const isFullQuadrant = unreplicatedTipCount.rowTipCount > 1 && unreplicatedTipCount.columnTipCount > 1;

      const [noSkipMode] = getSkipWellMode(
        unreplicatedTipCount.rowTipCount,
        unreplicatedTipCount.columnTipCount,
        isFullQuadrant,
        is384,
        true,
      );

      if (noSkipMode === SKIP_WELL_MODE.COLUMN) {
        const isSameColumnDifferentRow = (value) =>
          value.column === firstReferenceTask.column && value.row !== firstReferenceTask.row;

        secondReferenceTask = rowColumnList.find(isSameColumnDifferentRow);
      }

      if (noSkipMode === SKIP_WELL_MODE.ROW) {
        const isSameRowDifferentColumn = (value) =>
          value.row === firstReferenceTask.row && value.column !== firstReferenceTask.column;

        secondReferenceTask = rowColumnList.find(isSameRowDifferentColumn);
      }

      if (noSkipMode === SKIP_WELL_MODE.SKIP) {
        const isDifferentRowDifferentColumn = (value) =>
          value.row !== firstReferenceTask.row && value.column !== firstReferenceTask.column;

        secondReferenceTask = rowColumnList.find(isDifferentRowDifferentColumn);
      }
    }

    const swipeDirection = identifySwipeDirection(firstReferenceTask, secondReferenceTask);
    const basedAxis =
      swipeDirection.column && swipeDirection.row
        ? 'quadrant'
        : Object.keys(swipeDirection).find((key) => swipeDirection[key]);

    rowColumnList.forEach((e) => {
      if (e.row > labware?.rowCount || e.column > labware?.colCount) return;

      const axisBasedStep = registeredAxisBasedSelectedWell[e[basedAxis]];
      const taskStep = axisBasedStep?.step;
      const isNewStep = !taskStep;

      if (isNewStep) {
        step += 1;

        sdStepTasks[step] = [e];
        registeredAxisBasedSelectedWell[e[basedAxis]] = {
          step,
          tasks: [e],
        };
      } else {
        const newStepTasks = [...sdStepTasks[taskStep], e];

        sdStepTasks[taskStep] = newStepTasks;
        registeredAxisBasedSelectedWell[e[basedAxis]].tasks = newStepTasks;
      }
    });

    batch(async () => {
      await dispatch(createSdCardIifNotExist(cardID));

      const diluentTargetTasks = {};
      const diluentTargetTasksIds = [];
      let taskId;

      Object.keys(sdStepTasks).forEach((key) => {
        const stepRowColumnList = sdStepTasks[key];

        for (let i = 0, ii = stepRowColumnList.length; i < ii; i++) {
          const {row, column, tipCount} = stepRowColumnList[i];

          const [actualRow, actualColumn] = dispatch(
            taskListBusinessAction.getWellTarget(labwareID, TipChannelType.TIP_CHANNEL_1, row, column),
          );

          const {cardState: cardState2} = getState();
          const {cards: cards2, tasks: tasks2} = cardState2;

          if (!taskId) {
            const [, taskID2] = generateCardIDAndTaskIDAndNumber(cards2, tasks2, CardType.SERIAL_DILUTION);

            taskId = taskID2;
          } else {
            taskId += 1;
          }

          const diluentTargetTask = {...task, step: Number(key)};
          diluentTargetTask.ID = taskId;
          diluentTargetTask.row = actualRow;
          diluentTargetTask.column = actualColumn;
          if (tipCount) {
            diluentTargetTask.rowTipCount = tipCount.row;
            diluentTargetTask.columnTipCount = tipCount.column;
          }

          diluentTargetTasks[taskId] = diluentTargetTask;
          diluentTargetTasksIds.push(taskId);
        }
      });

      dispatch(cardSerialDilutionStateAction.addSdRawTasksDiluentTarget(cardID, diluentTargetTasks));

      const {cardState: cardState3} = getState();
      const {sdProperty: latestSDProperty} = find(cardState3.cards, {ID: cardID});

      const prevStepProperties = latestSDProperty?.stepProperties ?? [];
      const allDiluentVolumeValue = latestSDProperty?.diluentVolume === '-' ? 0 : latestSDProperty?.diluentVolume;
      const newStepProperties = [...prevStepProperties];

      for (let idx = 1; idx <= step; idx += 1) {
        const idxStep = idx;
        const otherProps = prevStepProperties.find((prop) => prop.step === idxStep) ?? {};
        const stepProperties = {
          stepSampleVolume: otherProps.stepSampleVolume || 0,
          step: idxStep,
          stepDiluentVolume: otherProps.stepDiluentVolume || allDiluentVolumeValue,
          ratio: otherProps?.ratio,
        };
        newStepProperties[idx - 1] = stepProperties;
      }

      if (latestSDProperty) {
        dispatch(
          cardSerialDilutionStateAction.setCardSdProperty(cardID, {
            ...latestSDProperty,
            step,
            stepProperties: newStepProperties,
          }),
        );
      }

      dispatch(saveState());
    });
  };
}

function removeSdRawTasksPerStep(cardId, sdStep) {
  return (dispatch, getState) => {
    if (!_.isNumber(cardId)) {
      return;
    }

    const {cardState} = getState();
    const {cards} = cardState;

    const selectedCard = find(cards, {ID: cardId});
    const sdRawTasksSummary = selectedCard?.sdRawTasksSummary;

    if (!sdRawTasksSummary) {
      return;
    }

    const {taskSampleSource, taskDiluentSource, taskSampleTarget, taskDiluentTargets} = sdRawTasksSummary;
    let toDeleteSdRawTaskIds = [];

    switch (sdStep) {
      case SerialDilutionStep.SET_SAMPLE_SOURCE:
        if (!_.isNumber(taskSampleSource)) {
          return;
        }

        toDeleteSdRawTaskIds.push(taskSampleSource);

        break;
      case SerialDilutionStep.SET_DILUENT_SOURCE:
        if (!_.isNumber(taskDiluentSource)) {
          return;
        }

        toDeleteSdRawTaskIds.push(taskDiluentSource);

        break;
      case SerialDilutionStep.SET_SAMPLE_TARGET:
        if (!taskSampleTarget || taskSampleTarget.length === 0) {
          return;
        }

        toDeleteSdRawTaskIds = toDeleteSdRawTaskIds.concat(taskSampleTarget);

        break;
      case SerialDilutionStep.SET_DILUENT_TARGET:
        if (!taskDiluentTargets || taskDiluentTargets.length === 0) {
          return;
        }

        toDeleteSdRawTaskIds = toDeleteSdRawTaskIds.concat(taskDiluentTargets);

        break;
      default:
        break;
    }

    if (toDeleteSdRawTaskIds.length === 0) {
      return;
    }

    batch(() => {
      for (let i = 0, ii = toDeleteSdRawTaskIds.length; i < ii; i++) {
        dispatch(cardSerialDilutionStateAction.removeSdRawTask(cardId, toDeleteSdRawTaskIds[i]));
      }
    });
  };
}

function removeSdRawTasksSummaryPerStep(cardId, sdStep) {
  return (dispatch, getState) => {
    if (!_.isNumber(cardId)) {
      return;
    }

    const {cardState} = getState();
    const {cards} = cardState;

    switch (sdStep) {
      case SerialDilutionStep.SET_SAMPLE_SOURCE:
        dispatch(cardSerialDilutionStateAction.setSdRawTasksSummarySampleSource(cardId, undefined));
        break;
      case SerialDilutionStep.SET_DILUENT_SOURCE:
        dispatch(cardSerialDilutionStateAction.setSdRawTasksSummaryDiluentSource(cardId, undefined));
        break;
      case SerialDilutionStep.SET_SAMPLE_TARGET:
        dispatch(cardSerialDilutionStateAction.setSdRawTasksSummarySampleTarget(cardId, []));
        break;
      case SerialDilutionStep.SET_DILUENT_TARGET: {
        dispatch(cardSerialDilutionStateAction.setSdRawTasksSummaryDiluentTargets(cardId, []));

        const existingSdProperty = cards[cardId]?.sdProperty;
        if (existingSdProperty) {
          // only reset the step
          const newSdProperty = {...existingSdProperty, step: null};
          dispatch(cardSerialDilutionStateAction.setCardSdProperty(cardId, newSdProperty));
        } else {
          dispatch(cardSerialDilutionStateAction.setCardSdProperty(cardId, {}));
        }

        break;
      }
      default:
        break;
    }
  };
}

export function removeSdRawTasks(cardId, sdSteps) {
  return (dispatch) => {
    batch(async () => {
      for (let i = 0, ii = sdSteps.length; i < ii; i++) {
        const sdStep = sdSteps[i];

        dispatch(removeSdRawTasksPerStep(cardId, sdStep));
        dispatch(removeSdRawTasksSummaryPerStep(cardId, sdStep));
      }

      dispatch(saveState());
    });
  };
}

export function removeSdDiluentTargetRawTask(cardId, taskIds) {
  return (dispatch) => {
    dispatch(cardSerialDilutionStateAction.removeDiluentTargetRawTask(cardId, taskIds));
    dispatch(saveState());
  };
}

export function skipSdRawTask(sdStep) {
  return (dispatch) => {
    dispatch(setSdRawTask(sdStep, null, null, null, null, true));
  };
}

export const createSdTemplate = () => (dispatch) => {
  Object.values(SerialDilutionStep).forEach((step) => {
    if (step !== SerialDilutionStep.SET_SAMPLE_TARGET && step !== SerialDilutionStep.SET_VOLUME_AND_RATIO) {
      dispatch(skipSdRawTask(step));
    }
  });
};

export function saveSdTemporaryStates(cardID, type) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    batch(() => {
      if (type === TemporaryStateType.EDIT_FROM_SD_SETTINGS) {
        const editSd = {cards: cards[cardID], tasks};
        dispatch(temporaryStateAction.addTemporaryState(TemporaryStateType.EDIT_FROM_SD_SETTINGS, editSd));
      } else {
        dispatch(temporaryStateAction.addTemporaryState(TemporaryStateType.CARDS, cards[cardID]));
        dispatch(temporaryStateAction.addTemporaryState(TemporaryStateType.TASKS, tasks));
      }
    });
  };
}

export function discardSdEdit(isFromSdSettings) {
  return (dispatch, getState) => {
    const {temporaryState, cardState} = getState();
    const {cards} = cardState;

    let tempCard;
    let tempTasks;
    if (isFromSdSettings) {
      const tempState = temporaryState[TemporaryStateType.EDIT_FROM_SD_SETTINGS];
      tempCard = tempState.cards;
      tempTasks = tempState.tasks;
    } else {
      tempCard = temporaryState[TemporaryStateType.CARDS];
      tempTasks = temporaryState[TemporaryStateType.TASKS];
    }

    const prevCards = {...cards, [tempCard.ID]: tempCard};
    const prevTasks = tempTasks;
    if (!prevCards || !prevTasks) return;

    batch(() => {
      dispatch(cardStateAction.restoreCardState(prevCards));
      dispatch(cardStateAction.restoreCardTaskState(prevTasks));
      dispatch(clearSerialDilutionTemporaryState(Boolean(temporaryState[TemporaryStateType.EDIT_FROM_SD_SETTINGS])));
    });
  };
}

export function isSdEditDirty() {
  return (dispatch, getState) => {
    const {cardState, temporaryState} = getState();
    const {cards, tasks} = cardState;

    const prevCards = temporaryState[TemporaryStateType.CARDS];
    const prevTasks = temporaryState[TemporaryStateType.TASKS];

    if (!prevCards || !prevTasks) {
      return false;
    }

    return !_.isEqual(cards, prevCards) || !_.isEqual(tasks, prevTasks);
  };
}

export function getSdWellGroupName(row, column, rowTipCount, columnTipCount, labwareRowCount, labwareColumnCount) {
  const labwareWellCount = labwareRowCount * labwareColumnCount;
  const tipCount = rowTipCount * columnTipCount;

  const is384Wells = labwareWellCount === WellCount.WELL_384;

  if (tipCount === labwareWellCount || labwareWellCount === 1) {
    return 'All Well';
  }

  if (tipCount === 1) {
    return `Well ${row}${column}`;
  }

  if (tipCount === 96) {
    return `Quadrant ${getQuadrant(row.charCodeAt(0) - 64, column)}`;
  }

  if (rowTipCount > 1) {
    if (rowTipCount < labwareRowCount) {
      const rowOffset = is384Wells ? (rowTipCount - 1) * 2 : rowTipCount - 1;

      return `${row}${column}:${convertRowToAlphabet(row.charCodeAt(0) - 64 + rowOffset)}${column}`;
    }

    return `Col ${column}`;
  }

  if (columnTipCount > 1) {
    if (columnTipCount < labwareColumnCount) {
      const columnOffset = is384Wells ? (columnTipCount - 1) * 2 : columnTipCount - 1;

      return `${row}${column}:${row}${column + columnOffset}`;
    }

    return `Row ${row}`;
  }

  console.error('getWellGroupName unmapped tip count', tipCount);

  return '';
}

export function getSdWellGroupNameByTask(task) {
  return (dispatch, getState) => {
    const {labwareState} = getState();
    const selectedLabware = find(labwareState, {LabwareID: task.labwareID});
    const {rowCount: labwareRowCount, colCount: labwareColumnCount} = selectedLabware;

    return getSdWellGroupName(
      task.row,
      task.column,
      task.rowTipCount,
      task.columnTipCount,
      labwareRowCount,
      labwareColumnCount,
    );
  };
}

export function setIgnoredSdRawTasks(sdStep, isIgnored) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const {sdRawTasks} = selectedCard;
    const {taskSampleSource, taskSampleTarget, taskDiluentSource, taskDiluentTargets} = selectedCard.sdRawTasksSummary;

    let selectedSdRawTasks = [];

    switch (sdStep) {
      case SerialDilutionStep.SET_SAMPLE_SOURCE:
        selectedSdRawTasks = filterToArray(sdRawTasks, {ID: taskSampleSource});
        break;
      case SerialDilutionStep.SET_SAMPLE_TARGET:
        selectedSdRawTasks = filterToArray(sdRawTasks, {ID: taskSampleTarget});
        break;
      case SerialDilutionStep.SET_DILUENT_SOURCE:
        selectedSdRawTasks = filterToArray(sdRawTasks, {ID: taskDiluentSource});
        break;
      case SerialDilutionStep.SET_DILUENT_TARGET:
        if (taskDiluentTargets && taskDiluentTargets.length > 0) {
          selectedSdRawTasks = filterToArray(sdRawTasks, {ID: taskDiluentTargets});
        }

        break;
      default:
        break;
    }

    if (selectedSdRawTasks.length === 0) {
      return;
    }

    batch(async () => {
      for (let i = 0, ii = selectedSdRawTasks.length; i < ii; i++) {
        dispatch(
          cardSerialDilutionStateAction.setIgnoredSdRawTask(selectedCard.ID, selectedSdRawTasks[i].ID, isIgnored),
        );
      }

      // #region handling for card checkbox

      // checked = true if at least one task is not ignored
      // checked = false if all tasks are ignored
      const {cardState: cardState2} = getState();
      const {cards: cards2} = cardState2;

      const selectedCard2 = find(cards2, {isSelected: true});

      const {sdRawTasks: sdRawTasks2} = selectedCard2;
      const anyNotIgnoredTasks = filterToArray(sdRawTasks2, {isIgnored: false}).length > 0;

      dispatch(cardStateAction.setIgnoredCard(selectedCard.ID, !anyNotIgnoredTasks));

      // #endregion handling for card checkbox

      dispatch(generateSdCompleteTasks(selectedCard.ID));
      dispatch(saveState());
    });
  };
}

export function setIgnoredAllSdRawTasks(cardID, isIgnored) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const selectedCard = find(cards, {ID: cardID});

    if (!selectedCard) {
      return;
    }

    const {sdRawTasks} = selectedCard;

    const selectedSdRawTasks = filterToArray(sdRawTasks);

    if (selectedSdRawTasks.length === 0) {
      return;
    }

    batch(async () => {
      dispatch(cardStateAction.setIgnoredCard(selectedCard.ID, isIgnored));

      for (let i = 0, ii = selectedSdRawTasks.length; i < ii; i++) {
        dispatch(
          cardSerialDilutionStateAction.setIgnoredSdRawTask(selectedCard.ID, selectedSdRawTasks[i].ID, isIgnored),
        );
      }

      dispatch(generateSdCompleteTasks(selectedCard.ID));
      dispatch(saveState());
    });
  };
}

export const isSdStepDefined = (cardId, step) => (dispatch) => {
  const [taskDiluentSource, taskSampleSource, taskSampleTarget, taskDiluentTargets] = dispatch(getSdRawTasks(cardId));

  switch (step) {
    case SerialDilutionStep.SET_DILUENT_SOURCE:
      return Boolean(taskDiluentSource) && !taskDiluentSource?.skipped;
    case SerialDilutionStep.SET_SAMPLE_SOURCE:
      return Boolean(taskSampleSource) && !taskSampleSource?.skipped;
    case SerialDilutionStep.SET_SAMPLE_TARGET:
      return Boolean(taskSampleTarget?.length);
    case SerialDilutionStep.SET_DILUENT_TARGET:
      return Boolean(taskDiluentTargets?.length);
    default:
      return false;
  }
};

export const generateTaskRatio = (taskList, sdProperty) => {
  let tasksRatio = '';
  if (sdProperty?.stepProperties) {
    const ratios = [];
    taskList.forEach((task) => {
      const stepProperty = sdProperty.stepProperties.find((prop) => prop.step === task.step);
      ratios.push(`[${stepProperty.ratio && stepProperty.ratio[0]}/${stepProperty.ratio && stepProperty.ratio[1]}]`);
    });
    tasksRatio = ratios.join('; ');
  } else {
    const ratios = [];
    taskList.forEach(() => {
      ratios.push(`[1/${sdProperty.ratio}]`);
    });

    tasksRatio = ratios.join('; ');
  }

  return tasksRatio;
};

export const generateDilutionTransferTasksSummary = (tasks, sdProperty) => (dispatch, getState) => {
  const {labwareState} = getState();

  const taskMap = {};
  const tasksGroup = [];

  for (let i = 0, ii = tasks.length; i < ii; i += 1) {
    const task = tasks[i];
    const taskLabware = find(labwareState, {LabwareID: task.labwareID});
    const labwareName = taskLabware?.name;

    if (!(labwareName in taskMap)) {
      taskMap[labwareName] = {labware: taskLabware, tasks: []};
    }

    taskMap[labwareName].tasks.push(task);
  }

  Object.keys(taskMap).forEach((labwareName) => {
    tasksGroup.push(
      <span key={`task-diluent-target-${labwareName}`}>
        &nbsp;
        <span className={`${getWordBreakClassName(labwareName, 19)}`}>{labwareName}</span>
        &nbsp;
        <span className="green">
          {`[${taskMap[labwareName].tasks
            .map((task) =>
              getSdWellGroupName(
                task.row,
                task.column,
                task.rowTipCount,
                task.columnTipCount,
                taskMap[labwareName].labware?.rowCount,
                taskMap[labwareName].labware?.colCount,
              ),
            )
            .join('; ')}]`}
        </span>
        &nbsp;
        <span>Concentration Ratio</span>
        &nbsp;
        <span className="blue">{generateTaskRatio(taskMap[labwareName].tasks, sdProperty)}</span>
      </span>,
    );
  });

  return tasksGroup;
};

export function getSdTaskExecutionStatus(sdStep, card) {
  return (dispatch, getState) => {
    const {runtimeState, cardState} = getState();
    const {status, csvRecordID} = runtimeState;
    const {tasks} = cardState;

    const cardExecutionStatus = dispatch(getCardExecutionStatus(card));

    // haven't been executed or have finished executing
    if (cardExecutionStatus !== ExecutionStatus.EXECUTING) {
      return cardExecutionStatus;
    }

    const runningTask = tasks[csvRecordID];
    const runningTaskSdStep = runningTask?.number;

    if (!runningTaskSdStep) {
      return ExecutionStatus.NONE;
    }

    if (status === RuntimeMode.ABORTED) {
      return ExecutionStatus.NONE;
    }

    if (card.type === CardType.SERIAL_DILUTION && !card.tasks.length) {
      return ExecutionStatus.NONE;
    }

    if (status === RuntimeMode.FINISH) {
      return ExecutionStatus.EXECUTED;
    }

    if (sdStep < runningTaskSdStep) {
      return ExecutionStatus.EXECUTED;
    }

    if (sdStep === runningTaskSdStep) {
      return ExecutionStatus.EXECUTING;
    }

    return ExecutionStatus.NONE;
  };
}

export const handleStepPropertiesChange = (key, stepNumber, newVal, cardID) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;
  const selectedCard = cards[cardID];

  if (newVal < 0) return;

  const {diluentVolume} = selectedCard?.sdProperty || {};
  const stepProperties = selectedCard?.sdProperty?.stepProperties ?? [];

  const property = stepProperties.find((prop) => prop?.step === stepNumber) || {step: stepNumber};
  let newStepProperty = {};

  switch (key) {
    case SDVolumePropertyKeyEnum.RATIO: {
      const ratioSample = newVal[0] || property?.ratio[0] || 1;
      const ratioTotal = ratioSample > newVal[1] && newVal[1] ? ratioSample : newVal[1] || property?.ratio[1] || 1;
      // ratio [1, 1] is used as the default, filled in, ratio
      // isChangeAffectedVolume will be true if user intentionally set the ratio as [1, 1]
      // if not, the ratio is the one that will be changed because it is assumed that the user haven't set the ratio yet (still default)
      const isChangeAffectedVolume = ratioSample === 1 && ratioTotal === 1;
      const persistentVolumeObject =
        property.stepSampleVolume === 0
          ? {stepDiluentVolume: property.stepDiluentVolume}
          : {stepSampleVolume: property.stepSampleVolume};

      newStepProperty = processStepVolumeChange(
        persistentVolumeObject,
        {...property, ratio: [ratioSample, ratioTotal]},
        false,
        isChangeAffectedVolume,
      );
      break;
    }
    default: {
      // ratio will change to [1, 1] if the step diluent volume is 0
      const isChangeRatio = key === SDVolumePropertyKeyEnum.STEP_DILUENT_VOLUME && newVal === 0;

      newStepProperty = processStepVolumeChange({[key]: newVal}, property, isChangeRatio);
      break;
    }
  }

  dispatch(cardSerialDilutionStateAction.setSdStepPropertiesObject(cardID, stepNumber, newStepProperty));

  if (stepNumber === 1) {
    const {stepSampleVolume} = newStepProperty;
    // eslint-disable-next-line no-lonely-if
    if (dispatch(isSdStepDefined(cardID, SerialDilutionStep.SET_SAMPLE_TARGET))) {
      dispatch(cardSerialDilutionStateAction.setSdPropertyObject(cardID, {aliquotVolume: stepSampleVolume}));
    } else if (dispatch(isSdStepDefined(cardID, SerialDilutionStep.SET_SAMPLE_SOURCE))) {
      dispatch(cardSerialDilutionStateAction.setSdPropertyObject(cardID, {sampleVolume: stepSampleVolume}));
    }
  }

  const newSdProperty = _.cloneDeep(selectedCard?.sdProperty || {});
  if (newSdProperty?.stepProperties) {
    newSdProperty.stepProperties[stepNumber - 1] = {...property, ...newStepProperty};

    const {isVolumeUniform} = isStepVolumeValueUniform(key, newSdProperty);
    if (!isVolumeUniform && key === SDVolumePropertyKeyEnum.STEP_DILUENT_VOLUME && diluentVolume !== '-') {
      dispatch(
        cardSerialDilutionStateAction.setSdPropertyObject(cardID, {[SDVolumePropertyKeyEnum.DILUENT_VOLUME]: '-'}),
      );
    }
  }

  dispatch(globalAddUndoItem());
};

export const handleSDPropertyChange = (key, value, cardID, totalStepCountRef) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;
  const {isSetSampleToAllSteps, stepProperties} = cards[cardID].sdProperty;

  if (Number.isNaN(value) || value < 0) return;

  dispatch(cardSerialDilutionStateAction.setSdPropertyObject(cardID, {[key]: value}));

  const isChangeAllStep =
    (key === SDVolumePropertyKeyEnum.ALIQUOT_VOLUME && isSetSampleToAllSteps) ||
    key === SDVolumePropertyKeyEnum.DILUENT_VOLUME;

  const isKeySampleVolume = key === SDVolumePropertyKeyEnum.ALIQUOT_VOLUME;

  if (isKeySampleVolume && !isChangeAllStep) {
    handleStepPropertiesChange(SDVolumePropertyKeyEnum.STEP_SAMPLE_VOLUME, 1, value);
  }

  const newVal = {[_.camelCase(`step_${isKeySampleVolume ? 'sampleVolume' : key}`)]: value};
  const newStepPropeties = [];

  const totalStep = totalStepCountRef.current < 4 || !totalStepCountRef.current ? 4 : totalStepCountRef.current;

  for (let i = 0; i < totalStep; i += 1) {
    const currentStepProperty = stepProperties ? stepProperties[i] || {} : {};
    const newProperty = processStepVolumeChange(newVal, currentStepProperty, true);
    newStepPropeties.push(newProperty);
  }
  if (key === SDVolumePropertyKeyEnum.ALIQUOT_VOLUME || key === SDVolumePropertyKeyEnum.DILUENT_VOLUME) {
    dispatch(
      cardSerialDilutionStateAction.setSdStepPropertiesObject(
        cardID,
        !isChangeAllStep && key === SDVolumePropertyKeyEnum.ALIQUOT_VOLUME
          ? [0, 1]
          : [...Array(totalStep + 1).keys()].slice(1),
        newStepPropeties,
      ),
    );
  }

  dispatch(globalAddUndoItem());
};

export const applyChangesToAllStep =
  (key, newVal, cardID, isParameterKeySdProperty = true) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;
    const selectedCard = cards[cardID];
    const {sdProperty: sdPropertyRedux} = selectedCard || {};

    const totalStep = sdPropertyRedux?.step || [];

    let stepVolumePropertyKey = key;

    if (isParameterKeySdProperty) {
      stepVolumePropertyKey = key === 'aliquotVolume' ? 'stepSampleVolume' : _.camelCase(`step_${key}`);
    }

    if (totalStep !== 0) {
      dispatch(
        cardSerialDilutionStateAction.setSdStepPropertiesObject(
          cardID,
          !sdPropertyRedux?.isSetSampleToAllSteps ? [0, 1] : [...Array(totalStep + 1).keys()].slice(1),
          {[stepVolumePropertyKey]: newVal},
        ),
      );
    }
  };

export const applyUniformedVolumeToNewSteps = (cardID) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;
  const selectedCard = cards[cardID];
  const {sdProperty} = selectedCard || {};
  const {stepProperties} = sdProperty || {};

  const keyToCheck = [
    SDVolumePropertyKeyEnum.SAMPLE_VOLUME,
    SDVolumePropertyKeyEnum.DILUENT_VOLUME,
    SDVolumePropertyKeyEnum.RATIO,
  ];

  keyToCheck.forEach((key) => {
    const stepKey = key !== SDVolumePropertyKeyEnum.RATIO ? _.camelCase(`step_${key}`) : key;
    const {isVolumeUniform, stepVolumeList} = isStepVolumeValueUniform(stepKey, sdProperty);

    let keyVolume;

    switch (key) {
      case SDVolumePropertyKeyEnum.SAMPLE_VOLUME:
        keyVolume = sdProperty[SDVolumePropertyKeyEnum.ALIQUOT_VOLUME];
        break;
      case SDVolumePropertyKeyEnum.RATIO:
        keyVolume = stepProperties && stepProperties[0]?.ratio;
        break;
      default:
        keyVolume = sdProperty[key];
    }

    if (
      (isVolumeUniform ||
        stepVolumeList.length === 0 ||
        (key === SDVolumePropertyKeyEnum.RATIO && stepVolumeList.length === 2)) &&
      keyVolume &&
      keyVolume !== '-'
    ) {
      dispatch(applyChangesToAllStep(stepKey, keyVolume, cardID, false));
    }
  });
};

export const getIsChangingSDWellCountAllowed = (sdStep, selectedCard) => (dispatch, getState) => {
  const {labwareState} = getState();

  const isAllowed = isChangingSDWellCountAllowed(sdStep, selectedCard, labwareState);
  return isAllowed;
};

export const getSerialDilutionTipCount = (sdStep, selectedCard) => (dispatch, getState) => {
  if (!sdStep || !selectedCard) {
    return [null, null];
  }
  const {
    sdRawTasksSummary,
    sdRawTasks,
    sdProperty: {replicateWell = 1},
  } = selectedCard;

  const taskSampleSourceId = sdRawTasksSummary?.taskSampleSource;
  const taskDiluentSourceId = sdRawTasksSummary?.taskDiluentSource;
  const taskSampleTargetId = sdRawTasksSummary?.taskSampleTarget && sdRawTasksSummary?.taskSampleTarget[0];
  const taskDiluentTargetId = sdRawTasksSummary?.taskDiluentTargets && sdRawTasksSummary.taskDiluentTargets[0];

  const tasksTipCounts = [];
  let sampleSourceLabware = null;

  if (_.isNumber(taskSampleSourceId) && sdStep !== SerialDilutionStep.SET_SAMPLE_SOURCE) {
    const taskSampleSource = sdRawTasks[taskSampleSourceId];
    const {columnTipCount, rowTipCount, labwareID} = taskSampleSource;

    sampleSourceLabware = getLabwareDataById(labwareID)(getState());
    const isFreeSelection = isSDPatternAllowFreeSelection(taskSampleSource, sampleSourceLabware);

    if (!taskSampleSource.skipped && !isFreeSelection) {
      tasksTipCounts.push({columnTipCount, rowTipCount});
    }
  }

  if (_.isNumber(taskDiluentSourceId) && sdStep !== SerialDilutionStep.SET_DILUENT_SOURCE) {
    const taskDiluentSource = sdRawTasks[taskDiluentSourceId];
    const {columnTipCount, rowTipCount, labwareID} = taskDiluentSource;

    const diluentSourceLabware = getLabwareDataById(labwareID)(getState());

    const isMultipleTroughsUsed = diluentSourceLabware?.isTrough && sampleSourceLabware?.isTrough;
    const isFreeSelection = isMultipleTroughsUsed
      ? doesLabwareHaveSameDirectionBase(diluentSourceLabware, sampleSourceLabware)
      : isSDPatternAllowFreeSelection(taskDiluentSource, diluentSourceLabware);

    if (!taskDiluentSource.skipped && !isFreeSelection) {
      tasksTipCounts.push({columnTipCount, rowTipCount});
    }
  }

  if (_.isNumber(taskSampleTargetId) && sdStep !== SerialDilutionStep.SET_SAMPLE_TARGET) {
    const taskSampleTarget = sdRawTasks[taskSampleTargetId];
    const {columnTipCount, rowTipCount} = taskSampleTarget;

    tasksTipCounts.push({columnTipCount, rowTipCount});
  }

  if (_.isNumber(taskDiluentTargetId)) {
    const taskDiluentTarget = sdRawTasks[taskDiluentTargetId];
    const unReplicatedTipCount = dispatch(unReplicateTipCount(taskDiluentTarget));

    tasksTipCounts.push(unReplicatedTipCount);
  }

  const sortedTipCounts = tasksTipCounts.sort((current, next) => {
    const currentTipCount = current.rowTipCount * current.columnTipCount;
    const nextTipCount = next.rowTipCount * next.columnTipCount;

    return nextTipCount - currentTipCount;
  });

  const biggestTipCount = sortedTipCounts.length ? sortedTipCounts[0] : {columnTipCount: null, rowTipCount: null};

  if (sdStep === SerialDilutionStep.SET_DILUENT_TARGET && replicateWell > 1 && sortedTipCounts.length) {
    if (biggestTipCount.rowTipCount < biggestTipCount.columnTipCount) {
      biggestTipCount.rowTipCount *= replicateWell;
    } else {
      biggestTipCount.columnTipCount *= replicateWell;
    }
  }

  return [biggestTipCount.rowTipCount, biggestTipCount.columnTipCount];
};

/**
 * normalized well hover and added well skips data
 * @typedef {Object} ProcessedWellResult
 * @property {WellItem} hoveredWell - processed hovered well
 * @property {boolean} skipWellMode
 * @property {boolean} isNoSkipWell
 */

/**
 * process serial dilution hovered well. the well will be normalized and adjusted accordingly
 *
 * @param {Object} params.labware
 * @param {number} params.labwareIndex
 * @param {number} params.row - hovered row
 * @param {number} params.column - hovered column
 * @param {boolean} params.is2DSelection - Flag to indicate if the user is using 2D selection
 * @param {boolean} params.isLockWellPattern - Flag to indicate wether the well pattern can be changed when the condition is met
 * @returns {ProcessedWellResult}
 */
export const processSerialDilutionHoveredWell =
  ({labware, row, column, is2DSelection = false, labwareIndex = 0}) =>
  (dispatch, getState) => {
    const sdStep = getSerialDilutionStep(getState());
    const selectedCard = getFirstSelectedCardSelector(getState());
    const {sdRawTasks} = selectedCard;

    const is384 = isLabware384(labware);

    const rawTasks = Object.values(sdRawTasks).filter((task) => task.labwareID === labware.LabwareID);

    const isDispensing =
      sdStep === SerialDilutionStep.SET_DILUENT_TARGET || sdStep === SerialDilutionStep.SET_SAMPLE_TARGET;

    const [rowTipCount, columnTipCount] = dispatch(getSerialDilutionTipCount(sdStep, selectedCard));

    const [skipWellMode, isNoSkipWell] = getSkipWellMode(
      rowTipCount,
      columnTipCount,
      is2DSelection && isDispensing,
      is384,
      true,
    );
    const [minPosition, maxPosition] = getMinMaxPositionFromTaskList(rawTasks, row, column, labware, skipWellMode);

    const [normalizedRow, normalizedColumn] = normalizeHighlightedWell(
      row,
      column,
      {row: rowTipCount, column: columnTipCount},
      labware,
      labwareIndex,
      false,
      maxPosition,
      minPosition,
      rawTasks,
      skipWellMode,
    );

    const hoveredWell = new WellItem(normalizedRow, normalizedColumn, rowTipCount, columnTipCount);

    return {
      hoveredWell,
      skipWellMode,
      isNoSkipWell,
    };
  };

/**
 * serial dilution labware validation result
 * @typedef {Object} validationResult
 * @property {WellItem} isPassed - is parameter passed every validation
 * @property {boolean} isLabwareValid
 * @property {boolean} isTipCountValid
 * @property {boolean} isTaskIntersected - is hovered well intersected with existing tasks
 */

/**
 * Validates labware for serial dilution.
 *
 * @param {Object} params.labware - The labware to be validated.
 * @param {WellItem} [params.wellData=new WellItem()] - The well data for validation.
 * @param {boolean} [params.is2DSelection=false] - Flag to indicate if the user is using 2D selection
 * @returns {validationResult}
 */
export const validateLabwareForSerialDilution =
  ({labware, wellData = new WellItem(), is2DSelection = false}) =>
  (dispatch, getState) => {
    const sdStep = getSerialDilutionStep(getState());
    const selectedCard = getFirstSelectedCardSelector(getState());
    const is384 = isLabware384(labware);

    const {rowTipCount, columnTipCount} = wellData;
    const {sdRawTasks: rawTasks} = selectedCard;

    const isDispensing =
      sdStep === SerialDilutionStep.SET_DILUENT_TARGET || sdStep === SerialDilutionStep.SET_SAMPLE_TARGET;

    const [skipWellMode] = getSkipWellMode(rowTipCount, columnTipCount, is2DSelection && isDispensing, is384, true);

    const SDValidator = SerialDilutionLabwareValidator(labware, wellData);

    const isLabwareValid = SDValidator.validateLabware(sdStep);
    const isTipCountValid = SDValidator.validateTipCount(is2DSelection);
    const isTaskIntersected = SDValidator.validateTaskIntersection(sdStep, rawTasks, skipWellMode);

    const isPassed = isLabwareValid && isTipCountValid && !isTaskIntersected;

    return {
      isLabwareValid,
      isTipCountValid,
      isTaskIntersected,
      isPassed,
    };
  };

export const handleSerialDilutionInvalidLabware = (labware, is2DSelection) => (dispatch) => {
  const {isIFC, isSpearBio, isTrough} = labware;
  const is384 = isLabware384(labware);

  if (isIFC) {
    dispatch(showSnackbar(Feedback.ERROR, parseMessage('protocolEditor.serialDilution.createNew.disallowIFCLabware')));

    return;
  }

  if (isSpearBio) {
    dispatch(
      showSnackbar(Feedback.ERROR, parseMessage('protocolEditor.serialDilution.createNew.disallowSpearBioLabware')),
    );

    return;
  }

  if (!is384 && is2DSelection) {
    dispatch(showSnackbar(Feedback.ERROR, parseMessage('protocolEditor.serialDilution.createNew.labwareMissMatch')));
    return;
  }

  if (isTrough) {
    dispatch(
      showSnackbar(Feedback.ERROR, parseMessage('protocolEditor.serialDilution.createNew.disallowTroughLabware')),
    );
  }
};

export const getSDRawTasksOnLabware =
  (labwareID, isIncludeCurrentStep = false) =>
  (dispatch, getState) => {
    const selectedCard = getFirstSelectedCardSelector(getState());
    const sdStep = getSerialDilutionStep(getState());

    const {sdRawTasks} = selectedCard;

    const rawTasks = Object.values(sdRawTasks).filter((task) => {
      if (isIncludeCurrentStep) {
        return task.labwareID === labwareID;
      }

      return task.labwareID === labwareID && task.number !== sdStep;
    });

    return rawTasks;
  };

export const updateSDReplicateWell = (replicateWell) => (dispatch, getState) => {
  const {cardState} = getState();
  const selectedCard = getFirstSelectedCard(cardState.cards);

  const targetDilutionTasksIDs = filterToArray(selectedCard.sdRawTasks, (task) => task.type === TaskType.DISPENSE).map(
    (ele) => ele.ID,
  );

  batch(() => {
    dispatch(removeSdDiluentTargetRawTask(selectedCard.ID, targetDilutionTasksIDs));
    dispatch(cardSerialDilutionStateAction.setSdReplicateWell(selectedCard.ID, replicateWell));
    dispatch(globalAddUndoItem());
  });
};

export const getSDStepLabwareIDs = (sdStep) => (dispatch, getState) => {
  const selectedCard = getFirstSelectedCardSelector(getState());
  const {sdRawTasks, sdRawTasksSummary} = selectedCard;

  const currentStepSummaryKey = getSDRawTasksSummaryKeyFromStep(sdStep);
  const currentStepRawTaskId = sdRawTasksSummary[currentStepSummaryKey];

  const currentStepRawTaskIdsArray = isArray(currentStepRawTaskId)
    ? sdRawTasksSummary[currentStepSummaryKey]
    : [sdRawTasksSummary[currentStepSummaryKey]];

  const currentStepLabwareIds = compact(currentStepRawTaskIdsArray).map((id) => sdRawTasks[id].labwareID);

  return currentStepLabwareIds;
};

export const getTroughUsedInSerialDilution = (selectedCard, isExcludeCurrentSdStep) => (dispatch, getState) => {
  const {sdRawTasksSummary, sdRawTasks} = selectedCard;
  const sdStep = getSerialDilutionStep(getState());

  const {taskDiluentSource: taskDiluentSourceId, taskSampleSource: taskSampleSourceId} = sdRawTasksSummary || {};

  const taskDiluentSource = sdRawTasks[taskDiluentSourceId] || {};
  const taskSampleSource = sdRawTasks[taskSampleSourceId] || {};

  const diluentSourceLabware = getLabwareDataById(taskDiluentSource?.labwareID)(getState());
  const sampleSourceLabware = getLabwareDataById(taskSampleSource?.labwareID)(getState());

  const troughLabwareUsed = [diluentSourceLabware, sampleSourceLabware];

  if (isExcludeCurrentSdStep) {
    if (sdStep === SerialDilutionStep.SET_DILUENT_SOURCE) {
      troughLabwareUsed.shift();
    }

    if (sdStep === SerialDilutionStep.SET_SAMPLE_SOURCE) {
      troughLabwareUsed.pop();
    }
  }

  const sortedTroughLabwareUsed = orderBy(
    compact(troughLabwareUsed),
    (labware) => labware.rowCount * labware.colCount,
    ['desc'],
  );

  return sortedTroughLabwareUsed;
};

export const getSDAllowedSwipeDirection = (swipePattern) => (dispatch, getState) => {
  const selectedCard = getFirstSelectedCardSelector(getState());
  const sdStep = getSerialDilutionStep(getState());
  const troughs = dispatch(getTroughUsedInSerialDilution(selectedCard, true));

  const isDoingDilution = sdStep === SerialDilutionStep.SET_DILUENT_TARGET;

  if (troughs.length) {
    const allowedPattern = getSDAllowedSwipeDirectionOnTrough(troughs, swipePattern, isDoingDilution);
    return allowedPattern;
  }

  return [SwipeSelectionDirection.ALL];
};

export const ensureSDSwipePatternValidity = (swipePattern, swipeDirection) => (dispatch, getState) => {
  const selectedCard = getFirstSelectedCardSelector(getState());
  const troughsLabwareUsed = dispatch(getTroughUsedInSerialDilution(selectedCard));
  const sdStep = getSerialDilutionStep(getState());

  const {row, column, rowTipCount, columnTipCount} = swipePattern;
  const isSwipeDirectionValid = swipeDirection.y && swipeDirection.x;

  if (troughsLabwareUsed && sdStep !== SerialDilutionStep.SET_DILUENT_TARGET && isSwipeDirectionValid) {
    const isColumnBased = isLabwareColumnBased(troughsLabwareUsed[0]);
    const isSwipeValid = isColumnBased ? swipeDirection.y : swipeDirection.x;

    if (!isSwipeValid) {
      // automatically snap it to its full row/column capacity, while maintaining opposite axis
      const adjustedRow = isColumnBased ? 1 : row;
      const adjustedCol = isColumnBased ? column : 1;

      const adjustedRowTipCount = isColumnBased ? 8 : rowTipCount;
      const adjustedColumnTipCount = isColumnBased ? columnTipCount : 12;

      const newSwipePattern = new WellItem(adjustedRow, adjustedCol, adjustedRowTipCount, adjustedColumnTipCount);

      return newSwipePattern;
    }
  }

  return swipePattern;
};

export const ensureSDSwipeValidity = (swipePattern, swipeDirection, allowedSwipeDirection) => (dispatch) => {
  const validSwipeDirection = getAdjustedSwipeDirectionByTipCount(swipePattern, swipeDirection, allowedSwipeDirection);
  const validSwipePattern = dispatch(ensureSDSwipePatternValidity(swipePattern, validSwipeDirection));

  return {
    swipeDirection: validSwipeDirection,
    swipePattern: validSwipePattern,
  };
};

export const handleSerialDilutionPlateCriteriaMismatch =
  ({replicateWell, rowTipCount, columnTipCount, labware}) =>
  (dispatch) => {
    if (rowTipCount > labware.rowCount && rowTipCount / replicateWell === 1) {
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          parseMessage('protocolEditor.serialDilution.replicateWells.columnBasedPlateCriteramismatch', {
            replicateWells: replicateWell,
          }),
        ),
      );
    } else {
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          parseMessage('protocolEditor.serialDilution.replicateWells.plateCriteramismatch', {
            replicateWells: replicateWell,
            colCount: columnTipCount,
            rowCount: rowTipCount,
          }),
        ),
      );
    }
  };

export const unReplicateTipCount =
  ({rowTipCount, columnTipCount}) =>
  (dispatch, getState) => {
    const selectedCard = getFirstSelectedCardSelector(getState());

    const {sdProperty} = selectedCard;
    const {replicateWell} = sdProperty;

    const unReplicatedTipCount = {rowTipCount, columnTipCount};

    if (columnTipCount / replicateWell === 1) {
      unReplicatedTipCount.columnTipCount /= replicateWell;
    }
    if (rowTipCount / replicateWell === 1) {
      unReplicatedTipCount.rowTipCount /= replicateWell;
    }

    return unReplicatedTipCount;
  };
